/**
 * @file Tooltip 悬停说明
 * @description 自定义浮层；fade；视口边界翻转。
 *
 * @whenToUse 图标按钮说明、截断文本完整内容。
 * @whenNotToUse 可交互菜单（用 DropdownMenu / Popover）。
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn.ts'
import { computeTooltipBox } from '@/lib/floating.ts'

export type TooltipProps = {
  /** 提示内容 */
  content: ReactNode
  /** 触发元素 */
  children: ReactNode
  className?: string
}

export function Tooltip({ content, children, className }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const tipId = useId()
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const update = () => {
    const trigger = triggerRef.current
    const tip = tipRef.current
    if (!trigger || !tip) return
    const box = computeTooltipBox(
      trigger.getBoundingClientRect(),
      tip.getBoundingClientRect(),
    )
    setPos({ top: box.top, left: box.left })
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => {
        update()
        setVisible(true)
      })
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => update()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  return (
    <>
      <span
        ref={triggerRef}
        className={cn('inline-flex', className)}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {mounted && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={tipRef}
              id={tipId}
              role="tooltip"
              className={cn(
                'pointer-events-none fixed z-50 max-w-xs rounded-md border border-border bg-raised px-2 py-1 text-xs text-foreground transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={{ top: pos.top, left: pos.left }}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
